import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { patchingApi, toPatchApiError } from '@/services/patching'
import type { PatchInventoryResource, TagMode, TagWriteResult, VmTag } from '@/types/patching'
import { TAG_DESCRIPTION_MAX, validateTagName } from '@/utils/patchValidation'
import styles from './Patching.module.css'

interface TagDialogProps {
  connectionId: number
  /** The VMs selected in the discovery table. */
  rows: PatchInventoryResource[]
  /** The tags that already exist for this connection: offered as you type, and what Add / Replace work on. */
  tags: VmTag[]
  onClose: () => void
  onSaved: (result: TagWriteResult, mode: TagMode) => void
}

const MODES: { value: TagMode; label: string; hint: string }[] = [
  { value: 'create', label: 'Create new tag', hint: 'A new tag that holds exactly the selected VMs.' },
  { value: 'add', label: 'Add to existing tag', hint: 'Keeps the tag’s current VMs and adds the selected ones.' },
  { value: 'replace', label: 'Replace tag membership', hint: 'The selected VMs become the tag’s only VMs.' },
]

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Tag the selected VMs: create a tag, add them to one, or replace one's VMs. The name is validated as you type with the
 * same rules as the server. Only eligible VMs are tagged; the others are listed so nobody wonders where they went.
 */
export function TagDialog({ connectionId, rows, tags, onClose, onSaved }: TagDialogProps) {
  const titleId = useId()
  const nameId = useId()
  const listId = useId()
  const descId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<TagMode>('create')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [serverFields, setServerFields] = useState<Record<string, string[]>>({})

  const eligible = useMemo(() => rows.filter((row) => row.eligible), [rows])
  const excluded = rows.filter((row) => !row.eligible)
  const existing = tags.find((tag) => tag.name === name)
  const selectedIds = new Set(eligible.map((row) => row.resource_id))
  const groups = [...new Set(eligible.map((row) => row.resource_group))].sort((a, b) => a.localeCompare(b))

  const nameProblems = validateTagName(name)
  let modeProblem: string | null = null
  if (nameProblems.length === 0) {
    if (mode === 'create' && existing) {
      modeProblem = `A tag named "${name}" already exists. Choose "Add to existing tag" or "Replace tag membership", or use another name.`
    } else if (mode !== 'create' && !existing) {
      modeProblem = `No tag is named "${name}". Pick one of the existing tags, or choose "Create new tag".`
    }
  }
  const descriptionTooLong = description.length > TAG_DESCRIPTION_MAX
  const nameMessages = [...(name === '' ? [] : nameProblems), ...(modeProblem ? [modeProblem] : []), ...(serverFields.name ?? [])]
  const canSave = !busy && eligible.length > 0 && nameProblems.length === 0 && modeProblem === null && !descriptionTooLong

  // What the chosen mode will do to an existing tag, said before anything is saved.
  const alreadyIn = existing ? existing.members.filter((m) => selectedIds.has(m.resource_id)).length : 0
  const dropped = existing ? existing.members.filter((m) => !selectedIds.has(m.resource_id)) : []

  useEffect(() => {
    document.getElementById(nameId)?.focus()
  }, [nameId])

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      if (!busy) onClose()
      return
    }
    if (event.key !== 'Tab' || !dialogRef.current) return
    const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!canSave) return
    setBusy(true)
    setServerError(null)
    setServerFields({})
    try {
      const result = await patchingApi.saveTag(mode, {
        connection_id: connectionId,
        name,
        description: description.trim(),
        resource_ids: eligible.map((row) => row.id),
        tag_id: existing?.id,
      })
      onSaved(result, mode)
    } catch (err) {
      const failure = toPatchApiError(err)
      setServerError(failure.message)
      setServerFields(failure.fieldErrors)
      setBusy(false)
    }
  }

  return (
    <div className={styles.dialogBackdrop}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <h2 id={titleId} className={styles.dialogTitle}>Add tags to selected VMs</h2>
        <p className={styles.muted} style={{ margin: 0 }}>
          {eligible.length} VM{eligible.length === 1 ? '' : 's'} will be tagged:{' '}
          <span className={styles.mono}>{eligible.slice(0, 5).map((row) => row.name).join(', ')}{eligible.length > 5 ? `, and ${eligible.length - 5} more` : ''}</span>.
          A campaign can then name the tag instead of listing these VMs. The tag is stored in this portal; nothing changes in Azure.
        </p>

        {groups.length > 1 && (
          <p className={styles.hint} style={{ margin: 0 }} data-testid="tag-groups-note">
            They are in {groups.length} resource groups ({groups.join(', ')}). A tag groups VMs whatever resource group they are in.
          </p>
        )}

        {excluded.length > 0 && (
          <div className={clsx(styles.alert, styles.alertWarning)} role="alert" data-testid="tag-excluded">
            <AlertTriangle size={16} className={styles.alertIcon} aria-hidden="true" />
            <span>
              Not eligible for patching, so {excluded.length === 1 ? 'it is' : 'they are'} left out:{' '}
              {excluded.map((row) => `${row.name} (${row.exclusion_reason || 'not eligible'})`).join('; ')}
            </span>
          </div>
        )}

        <form onSubmit={onSubmit} className={styles.stack} noValidate>
          <fieldset className={styles.radioGroup}>
            <legend className={styles.label}>What do you want to do?</legend>
            {MODES.map((option) => (
              <label key={option.value} className={styles.checkboxRow}>
                <input
                  type="radio"
                  name="tag-mode"
                  value={option.value}
                  checked={mode === option.value}
                  disabled={option.value !== 'create' && tags.length === 0}
                  onChange={() => setMode(option.value)}
                />
                <span>
                  {option.label}
                  <span className={styles.hint} style={{ display: 'block' }}>
                    {option.value !== 'create' && tags.length === 0 ? 'There are no tags to change yet.' : option.hint}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className={styles.field}>
            <label className={styles.label} htmlFor={nameId}>
              Tag name <span aria-hidden="true">*</span>
            </label>
            <input
              id={nameId}
              className={clsx(styles.input, nameMessages.length > 0 && styles.inputError)}
              value={name}
              list={listId}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={nameMessages.length > 0 || undefined}
              aria-describedby={`${nameId}-help`}
              onChange={(event) => {
                setName(event.target.value)
                setServerFields({})
              }}
            />
            <datalist id={listId}>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.name}>{`${tag.member_count} VM${tag.member_count === 1 ? '' : 's'}`}</option>
              ))}
            </datalist>
            <div id={`${nameId}-help`}>
              {nameMessages.length > 0 ? (
                <ul className={styles.fieldError} data-testid="tag-name-errors">
                  {nameMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : (
                <span className={styles.hint}>3-50 characters: lowercase letters, digits, hyphens and underscores. An exact name, never a pattern.</span>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor={descId}>Description (optional)</label>
            <textarea
              id={descId}
              className={styles.textarea}
              value={description}
              maxLength={TAG_DESCRIPTION_MAX + 50}
              onChange={(event) => setDescription(event.target.value)}
            />
            {descriptionTooLong && <span className={styles.fieldError}>A description is at most {TAG_DESCRIPTION_MAX} characters.</span>}
          </div>

          {existing && mode === 'add' && (
            <p className={styles.hint} role="status">
              “{existing.name}” has {existing.member_count} VM{existing.member_count === 1 ? '' : 's'}; {alreadyIn} of the selected
              {alreadyIn === 1 ? ' is' : ' are'} already in it, so {eligible.length - alreadyIn} will be added.
            </p>
          )}
          {existing && mode === 'replace' && (
            <div className={clsx(styles.alert, styles.alertWarning)} role="status" data-testid="tag-replace-warning">
              <AlertTriangle size={16} className={styles.alertIcon} aria-hidden="true" />
              <span>
                {dropped.length === 0
                  ? `“${existing.name}” already holds exactly these VMs.`
                  : `Replacing removes ${dropped.length} VM${dropped.length === 1 ? '' : 's'} from “${existing.name}”: ${dropped.map((m) => m.vm_name).join(', ')}.`}{' '}
                Campaigns already created from it keep the VMs they were created with.
              </span>
            </div>
          )}

          {serverError && (
            <div className={clsx(styles.alert, styles.alertError)} role="alert">{serverError}</div>
          )}
          {(serverFields.resource_ids ?? []).map((message) => (
            <div key={message} className={clsx(styles.alert, styles.alertError)} role="alert">{message}</div>
          ))}

          <div className={styles.actionRow}>
            <Button type="submit" disabled={!canSave} loading={busy}>Save tag</Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  )
}
